import { 
  API_CONFIG, 
  buildWCUrl, 
  formatPhoneNumber,
  type APIResponse
} from '../../config/api.enhanced';

// Types for authentication
export interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  is_new_user?: boolean;
  registered_date?: string; // تاريخ التسجيل من قاعدة البيانات
}

export interface AuthTokenData {
  token: string;
  user: User;
  expires_in: number;
}

export interface OTPResponse {
  phone_masked: string;
  test_mode: boolean;
  expires_in: number;
  otp_for_testing?: string;
  wait_seconds?: number;
  daily_limit?: number;
  count?: number;
}

export interface VerifyOTPResponse {
  token: string;
  user: User;
  is_new_user: boolean;
  needs_completion: boolean;
  needs_registration?: boolean;
  email_updated?: boolean;
  email_conflict?: boolean;
}

/**
 * Authentication Service
 * Handles all authentication-related API calls
 * Works with Zustand store for state management
 */
class AuthService {
  private language: string = API_CONFIG.DEFAULT_LANGUAGE;

  private getToken(): string | null {
    // Always get token from localStorage (synced with Zustand)
    return localStorage.getItem('auth_token');
  }

  setLanguage(lang: string): void {
    if (API_CONFIG.SUPPORTED_LANGUAGES.includes(lang)) {
      this.language = lang;
    }
  }

  /**
   * Check if user is authenticated by checking localStorage
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  /**
   * Logout user - Clear token from localStorage and revoke on server
   */
  async logout(): Promise<void> {
    const token = this.getToken();
    
    if (token) {
      try {
        // Call backend to revoke JWT token
        await fetch(
          buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_LOGOUT),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ lang: this.language })
          }
        );

      } catch (error) {

        // Continue with local logout anyway
      }
    }
    
    // Clear token from localStorage (Zustand will handle the rest)
    localStorage.removeItem('auth_token');

  }

  /**
   * ✨ Attempt to refresh JWT Token
   * Called automatically when token expires (401 response)
   */
  private async attemptTokenRefresh(): Promise<boolean> {
    try {
      const oldToken = this.getToken();
      if (!oldToken) {

        return false;
      }

const response = await fetch(
        buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_REFRESH),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${oldToken}`
          },
          body: JSON.stringify({ lang: this.language })
        }
      );

      const data = await response.json();

      if (data.success && data.data?.token) {
        // Store new token
        localStorage.setItem('auth_token', data.data.token);

return true;
      }

return false;
    } catch (error) {

      return false;
    }
  }

  private async request<T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), API_CONFIG.REQUEST_TIMEOUT);
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      // ✅ Add JWT Token to Authorization header
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      const data = await response.json();

      const isInvalidTokenResponse = (status: number, payload: any): boolean => {
        if (!(status === 401 || status === 403)) {
          return false;
        }
        const code = (payload?.code ?? payload?.data?.code ?? '') as string;
        const message = (payload?.message ?? payload?.data?.message ?? '') as string;
        if (code === 'jwt_auth_invalid_token') {
          return true;
        }
        if (/token\s+was\s+revoked\s+or\s+replaced/i.test(message)) {
          return true;
        }
        if (/token\s+expired/i.test(message)) {
          return true;
        }
        if (/jwt/i.test(code)) {
          return true;
        }
        return false;
      };

      // ✅ Handle JWT token invalid/expired/revoked (401/403)
      if (isInvalidTokenResponse(response.status, data)) {

        const refreshed = await this.attemptTokenRefresh();
        
        if (refreshed) {
          // Retry original request with new token
          headers['Authorization'] = `Bearer ${this.getToken()}`;
          const retryResponse = await fetch(url, { ...options, headers, signal: controller.signal });
          const retryData = await retryResponse.json();
          
          clearTimeout(timeout);
          
          const isWrapped = retryData && typeof retryData === 'object' && ('data' in retryData || 'message' in retryData || 'success' in retryData);
          const payload = isWrapped && 'data' in retryData ? retryData.data : retryData;
          const message = (isWrapped ? retryData.message : undefined) || retryResponse.statusText || undefined;
          const ok = (isWrapped && typeof retryData.success === 'boolean') ? retryData.success : retryResponse.ok;
          
          return {
            success: ok,
            data: payload,
            message,
            language: this.language,
            timestamp: new Date().toISOString()
          };
        } else {
          // Logout user if refresh failed
          this.logout();
          return {
            success: false,
            message: 'انتهت الجلسة. الرجاء تسجيل الدخول مجدداً.'
          };
        }
      }

      // Unwrap common WP response shape { success, data, message }
      const isWrapped = data && typeof data === 'object' && ('data' in data || 'message' in data || 'success' in data);
      const payload = isWrapped && 'data' in data ? data.data : data;
      const message = (isWrapped ? data.message : undefined) || response.statusText || undefined;
      const ok = (isWrapped && typeof data.success === 'boolean') ? data.success : response.ok;

      if (!ok) {
        return {
          success: false,
          message: message || `HTTP ${response.status}: ${response.statusText}`,
          data: payload
        };
      }

      clearTimeout(timeout);
      return {
        success: true,
        data: payload,
        message,
        language: this.language,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      const message = (error as any)?.name === 'AbortError' ? 'Request timeout' : (error instanceof Error ? error.message : 'Network error');

      return {
        success: false,
        message,
      };
    }
  }

  /**
   * Send OTP to phone number
   */
  async sendOtp(phone: string, captchaToken?: string): Promise<APIResponse<OTPResponse>> {
    try {
      const url = buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_SEND_OTP);
      const body = {
        phone: formatPhoneNumber(phone),
        lang: this.language,
        captcha_token: captchaToken
      };

const response = await this.request<OTPResponse>(url, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      
      return response;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Verify OTP and login/register user
   */
  async verifyOtp(
    phone: string, 
    otp: string, 
    additionalData?: {
      name?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      password?: string;
      captchaToken?: string;
    }
  ): Promise<APIResponse<VerifyOTPResponse>> {
    try {
      const body: any = {
        phone: formatPhoneNumber(phone),
        otp_code: otp,
        lang: this.language
      };
      
      // Add optional data
      if (additionalData) {
        if (additionalData.name) body.name = additionalData.name;
        if (additionalData.firstName) body.first_name = additionalData.firstName;
        if (additionalData.lastName) body.last_name = additionalData.lastName;
        if (additionalData.email) body.email = additionalData.email;
        if (additionalData.password) body.password = additionalData.password;
        if (additionalData.captchaToken) body.captcha_token = additionalData.captchaToken;
      }

      const response = await this.request<VerifyOTPResponse>(
        buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_VERIFY_OTP),
        {
          method: 'POST',
          body: JSON.stringify(body)
        }
      );

      // Token will be managed by Zustand store
      return response;
    } catch (error) {

      throw error;
    }
  }


    /**
     * Send OTP to email address
     */
    async sendEmailOtp(email: string, captchaToken?: string): Promise<APIResponse<OTPResponse>> {
      const body = {
        email: email.trim(),
        lang: this.language,
        captcha_token: captchaToken,
      };

      return this.request<OTPResponse>(
        buildWCUrl((API_CONFIG.ENDPOINTS as any).AUTH_SEND_EMAIL_OTP),
        {
          method: 'POST',
          body: JSON.stringify(body)
        }
      );
    }

    /**
     * Verify email OTP and login/register user
     */
    async verifyEmailOtp(
      email: string,
      otp: string,
      additionalData?: {
        name?: string;
        firstName?: string;
        lastName?: string;
        captchaToken?: string;
      }
    ): Promise<APIResponse<VerifyOTPResponse>> {
      const body: any = {
        email: email.trim(),
        otp_code: otp,
        lang: this.language,
      };

      if (additionalData) {
        if (additionalData.name) body.name = additionalData.name;
        if (additionalData.firstName) body.first_name = additionalData.firstName;
        if (additionalData.lastName) body.last_name = additionalData.lastName;
        if (additionalData.captchaToken) body.captcha_token = additionalData.captchaToken;
      }

      return this.request<VerifyOTPResponse>(
        buildWCUrl((API_CONFIG.ENDPOINTS as any).AUTH_VERIFY_EMAIL_OTP),
        {
          method: 'POST',
          body: JSON.stringify(body)
        }
      );
    }
  /**
   * Login with email and password
   */
  async loginWithEmail(email: string, password: string, captchaToken?: string): Promise<APIResponse<AuthTokenData>> {
    try {
      const response = await this.request<AuthTokenData>(
        buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_LOGIN),
        {
          method: 'POST',
          body: JSON.stringify({ email, password, lang: this.language, captcha_token: captchaToken })
        }
      );

      // Token will be managed by Zustand store
      return response;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Forgot password - send reset link
   */
  async forgotPassword(email: string, captchaToken?: string): Promise<APIResponse<{ email: string }>> {
    try {
      const response = await this.request<{ email: string }>(
        buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_FORGOT),
        {
          method: 'POST',
          body: JSON.stringify({ email, lang: this.language, captcha_token: captchaToken })
        }
      );
      return response;
    } catch (error) {
      return { success: false, message: error instanceof Error ? error.message : 'Network error' };
    }
  }

  // Password reset by email code: request
  async requestPasswordReset(email: string, captchaToken?: string): Promise<APIResponse<{ email: string; expires_in: number; debug_code?: string }>> {
    return this.request(
      buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_PW_REQUEST),
      {
        method: 'POST',
        body: JSON.stringify({ email, captcha_token: captchaToken, lang: this.language })
      }
    );
  }

  // Password reset by email code: verify
  async verifyPasswordReset(email: string, code: string): Promise<APIResponse<{ reset_session: string; expires_in: number }>> {
    return this.request(
      buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_PW_VERIFY),
      {
        method: 'POST',
        body: JSON.stringify({ email, code, lang: this.language })
      }
    );
  }

  // Password reset by email code: complete
  async completePasswordReset(email: string, newPassword: string, resetSession: string): Promise<APIResponse<{ email: string }>> {
    return this.request(
      buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_PW_COMPLETE),
      {
        method: 'POST',
        body: JSON.stringify({ email, new_password: newPassword, reset_session: resetSession, lang: this.language })
      }
    );
  }

  /**
   * Get current user information
   */
  async getUserInfo(): Promise<APIResponse<User>> {
    const token = this.getToken();
    if (!token) {
      return {
        success: false,
        message: 'No auth token found'
      };
    }

    try {
      const response = await this.request<any>(
        buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_ME, { lang: this.language })
      );

      // Backend shape for /me is typically: { is_logged_in: true, user: { ... } }
      const maybeUser = (response as any)?.data?.user;
      if (response?.success && maybeUser && typeof maybeUser === 'object') {
        return {
          ...response,
          data: maybeUser as User,
        };
      }

      return response as APIResponse<User>;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Update current user's email (JWT required)
   * Intended to replace placeholder emails like @boat.local
   */
  async updateEmail(email: string): Promise<APIResponse<{ email: string; email_updated?: boolean; email_conflict?: boolean }>> {
    try {
      return await this.request(
        buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_UPDATE_EMAIL),
        {
          method: 'POST',
          body: JSON.stringify({ email, lang: this.language })
        }
      );
    } catch (error) {
      return { success: false, message: error instanceof Error ? error.message : 'Network error' };
    }
  }

  /**
   * Refresh token (JWT implementation)
   */
  async refreshToken(): Promise<APIResponse<AuthTokenData>> {
    const token = this.getToken();
    if (!token) {
      return {
        success: false,
        message: 'No token to refresh'
      };
    }

    try {
      const response = await fetch(
        buildWCUrl(API_CONFIG.ENDPOINTS.AUTH_REFRESH),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ lang: this.language })
        }
      );

      const data = await response.json().catch(() => ({}));

      // If backend explicitly says token is invalid/expired/revoked, clear token.
      // Otherwise (network/proxy/endpoint issues), DO NOT clear the token.
      const isInvalidToken = (() => {
        if (response.status === 401 || response.status === 403) return true;
        const code = (data?.code ?? data?.data?.code ?? '') as string;
        const message = (data?.message ?? data?.data?.message ?? '') as string;
        if (code === 'jwt_auth_invalid_token') return true;
        if (/token\s+was\s+revoked\s+or\s+replaced/i.test(message)) return true;
        if (/token\s+expired/i.test(message)) return true;
        if (/jwt/i.test(code)) return true;
        return false;
      })();

      if (data.success && data.data?.token) {
        // Store new token
        localStorage.setItem('auth_token', data.data.token);

return {
          success: true,
          data: {
            token: data.data.token,
            user: {} as User, // Will be fetched separately if needed
            expires_in: data.data.expires_in || 604800
          },
          message: data.message || 'Token refreshed successfully'
        };
      }

      if (isInvalidToken) {
        localStorage.removeItem('auth_token');
      }

      return {
        success: false,
        message: data.message || 'Token refresh failed'
      };
    } catch (error) {
      // Network/proxy failures should not force logout.
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Token refresh failed'
      };
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
